import React, { useEffect, useMemo, useState } from "react";
import api, { fmtInt } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth, can } from "@/auth";
import { PageHeader, Tile, Loading, OperatorLink } from "@/components/Bits";
import { ExportColumnsModal } from "@/components/Extras";
import ImportExportBar from "@/components/ImportExportBar";
import { useCountry } from "@/country";
import { Plus, PencilSimple, Trash, DownloadSimple } from "@phosphor-icons/react";

const SUB_SYSTEMS = [
  "Air Compressor", "Air Dryer", "Brake Control Unit", "Brake Disk", "Brake System",
  "Coupler (Automatic)", "Coupler (Hook)", "Coupler (Semi)", "Deadman", "Door Panels",
  "Elastomers", "Event Recorder", "Ground Contact", "HVAC Cabin", "HVAC Saloon",
  "Master Controller", "PIS", "Pantograph", "Sanding System", "Speed Sensor",
  "Third Rail", "Tread Brake Unit",
];

const EMPTY = {
  city: "", operator: "", line: "", platform: "",
  start_year: "", trainsets: 0, cars_per_trainset: 0,
  sub_system: "Air Compressor", qty_per_trainset: 1, total_qty: 0,
  supplier_raw: "", supplier: "", country: "",
  wabtec: false, maintenance_interval: "",
  last_ovh: "", last_maintainer: "", next_ovh: "",
};

export default function InstalledBase() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { code: country } = useCountry();
  const editable = can(user, "admin");
  const [ref, setRef] = useState(null);
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);

  const reloadRef = () => api.get("/reference").then((r) => setRef(r.data));
  const load = () => {
    const params = {};
    for (const k of ["city", "operator", "platform", "sub_system", "supplier"]) {
      if (filters[k]) params[k] = filters[k];
    }
    return api.get("/installed_base", { params }).then((r) => setRows(r.data.rows));
  };

  useEffect(() => { reloadRef(); }, []);
  useEffect(() => { load(); }, [filters]);

  const summary = useMemo(() => {
    if (!rows) return null;
    let total = 0, wabtec = 0;
    for (const r of rows) {
      total += r.total_qty || 0;
      if (r.wabtec) wabtec += r.total_qty || 0;
    }
    return { rows: rows.length, total, wabtec };
  }, [rows]);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...EMPTY, ...r, start_year: r.start_year ?? "", last_ovh: r.last_ovh ?? "", next_ovh: r.next_ovh ?? "" });
    setEditingId(r.id);
    setOpen(true);
  };

  const save = async () => {
    const body = {
      ...form,
      start_year: form.start_year ? parseInt(form.start_year) : null,
      trainsets: parseInt(form.trainsets) || 0,
      cars_per_trainset: parseInt(form.cars_per_trainset) || 0,
      qty_per_trainset: parseFloat(form.qty_per_trainset) || 1,
      total_qty: parseInt(form.total_qty) || 0,
      last_ovh: form.last_ovh ? parseInt(form.last_ovh) : null,
      next_ovh: form.next_ovh ? parseInt(form.next_ovh) : null,
      wabtec: !!form.wabtec,
    };
    if (editingId) await api.put(`/installed_base/${editingId}`, body);
    else await api.post("/installed_base", body);
    setOpen(false); setEditingId(null);
    await reloadRef(); await load();
  };

  const del = async (id) => {
    if (!confirm(t("ib.confirmDelete"))) return;
    await api.delete(`/installed_base/${id}`);
    await load();
  };

  if (!ref) return <Loading />;

  const FilterSel = ({ k, opts, label }) => (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <select
        value={filters[k] || ""}
        onChange={(e) => setFilters((f) => ({ ...f, [k]: e.target.value || undefined }))}
        className="input-tech"
        data-testid={`filter-${k}`}
      >
        <option value="">{t("filters.all")}</option>
        {opts.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </div>
  );

  return (
    <div data-testid="installed-base-page">
      <PageHeader
        title={t("nav.installedBase")}
        subtitle={t("ib.subtitle")}
        right={
          <div className="flex items-center gap-2">
            <button onClick={() => setExportOpen(true)} className="btn-ghost flex items-center gap-2" data-testid="ib-export">
              <DownloadSimple size={14} /> {t("export.button")}
            </button>
            <ImportExportBar
              entity="installed_base"
              country={country}
              canImport={editable}
              previewCols={[
                { key: "operator", label: t("table.operator") || "Operator" },
                { key: "city",     label: t("table.city") || "City" },
                { key: "platform", label: "Platform" },
                { key: "sub_system", label: "Sub-system" },
                { key: "total_qty", label: "Qty" },
              ]}
              onImported={() => load()}
            />
            {editable && (
              <button onClick={openNew} className="btn-accent flex items-center gap-2" data-testid="ib-new">
                <Plus size={14} weight="bold" /> {t("ib.newBtn")}
              </button>
            )}
          </div>
        }
      />
      <div className="p-4 md:p-10 space-y-5 md:space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 stagger">
          <FilterSel k="city" opts={ref.cities} label={t("filters.city")} />
          <FilterSel k="operator" opts={ref.operators} label={t("filters.operator")} />
          <FilterSel k="platform" opts={ref.platforms} label={t("filters.platform")} />
          <FilterSel k="sub_system" opts={ref.sub_systems} label={t("filters.sub")} />
          <FilterSel k="supplier" opts={ref.suppliers} label={t("filters.supplier")} />
          <div className="flex items-end">
            <button onClick={() => setFilters({})} className="btn-ghost w-full" data-testid="clear-filters">
              {t("filters.clear")}
            </button>
          </div>
        </div>

        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-5 stagger">
            <div className="tile p-5 md:p-6">
              <div className="label-tech">{t("ib.summaryRows")}</div>
              <div className="num text-[24px] md:text-[28px] font-semibold mt-2" style={{ letterSpacing: "-0.025em" }}>{fmtInt(summary.rows)}</div>
            </div>
            <div className="tile p-5 md:p-6">
              <div className="label-tech">{t("ib.summaryTotal")}</div>
              <div className="num text-[24px] md:text-[28px] font-semibold mt-2" style={{ letterSpacing: "-0.025em" }}>{fmtInt(summary.total)}</div>
            </div>
            <div className="tile p-5 md:p-6">
              <div className="label-tech">{t("ib.summaryWabtec")}</div>
              <div className="num text-[24px] md:text-[28px] font-semibold mt-2" style={{ color: "var(--accent)", letterSpacing: "-0.025em" }}>{fmtInt(summary.wabtec)}</div>
            </div>
          </div>
        )}

        <div className="tile overflow-auto p-0" style={{ maxHeight: "70vh" }}>
          {!rows ? <Loading /> : (
            <table className="tech" data-testid="installed-base-table">
              <thead>
                <tr>
                  <th>{t("table.id")}</th>
                  <th>{t("table.city")}</th>
                  <th>{t("table.operator")}</th>
                  <th>{t("table.platform")}</th>
                  <th>{t("table.sub")}</th>
                  <th className="text-right">{t("table.qty")}</th>
                  <th>{t("table.supplier")}</th>
                  <th>{t("table.wabtec")}</th>
                  <th>{t("table.ovh")}</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 1000).map((r) => (
                  <tr key={r.id}>
                    <td className="font-mono text-[12px]" style={{ color: "var(--text-tertiary)" }}>{r.id}</td>
                    <td>{r.city}</td>
                    <td><OperatorLink name={r.operator} /></td>
                    <td>{r.platform}</td>
                    <td>{r.sub_system}</td>
                    <td className="num">{fmtInt(r.total_qty)}</td>
                    <td>{r.supplier || "—"}</td>
                    <td>
                      {r.wabtec ? (
                        <span className="pill pill-hot">YES</span>
                      ) : (
                        <span className="text-[12px]" style={{ color: "var(--text-tertiary)" }}>—</span>
                      )}
                    </td>
                    <td className="font-mono text-[12px]">{r.next_ovh || "—"}</td>
                    {editable && (
                      <td className="text-right whitespace-nowrap">
                        <button onClick={() => openEdit(r)} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid={`ib-edit-${r.id}`}>
                          <PencilSimple size={14} />
                        </button>
                        <button onClick={() => del(r.id)} className="p-1.5 rounded-full hover:bg-[var(--bg-subtle)]" data-testid={`ib-del-${r.id}`}>
                          <Trash size={14} style={{ color: "var(--accent)" }} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {open && (
        <Modal onClose={() => setOpen(false)} title={editingId ? `${t("ib.editTitle")} ${editingId}` : t("ib.newTitle")} testId="ib-modal">
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <Field label={t("table.city")} v={form.city} set={(v) => setForm({ ...form, city: v })} />
            <Field label={t("table.operator")} v={form.operator} set={(v) => setForm({ ...form, operator: v })} />
            <Field label="Line" v={form.line} set={(v) => setForm({ ...form, line: v })} />
            <Field label={t("table.platform")} v={form.platform} set={(v) => setForm({ ...form, platform: v })} />
            <Field label="Start year" type="number" v={form.start_year} set={(v) => setForm({ ...form, start_year: v })} />
            <Field label="Trainsets" type="number" v={form.trainsets} set={(v) => setForm({ ...form, trainsets: v })} />
            <Field label="Cars / trainset" type="number" v={form.cars_per_trainset} set={(v) => setForm({ ...form, cars_per_trainset: v })} />
            <FieldSel label={t("table.sub")} v={form.sub_system} set={(v) => setForm({ ...form, sub_system: v })} opts={SUB_SYSTEMS} />
            <Field label="Qty / trainset" type="number" v={form.qty_per_trainset} set={(v) => setForm({ ...form, qty_per_trainset: v })} />
            <Field label="Total Qty" type="number" v={form.total_qty} set={(v) => setForm({ ...form, total_qty: v })} />
            <Field label="Supplier (raw)" v={form.supplier_raw} set={(v) => setForm({ ...form, supplier_raw: v })} />
            <Field label={t("table.supplier")} v={form.supplier} set={(v) => setForm({ ...form, supplier: v })} />
            <Field label="Country" v={form.country} set={(v) => setForm({ ...form, country: v })} />
            <Field label="Maintenance interval" v={form.maintenance_interval} set={(v) => setForm({ ...form, maintenance_interval: v })} />
            <Field label="Last OVH (year)" type="number" v={form.last_ovh} set={(v) => setForm({ ...form, last_ovh: v })} />
            <Field label="Next OVH (year)" type="number" v={form.next_ovh} set={(v) => setForm({ ...form, next_ovh: v })} />
            <div className="col-span-2 flex items-center gap-3 pt-2">
              <label className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={!!form.wabtec}
                  onChange={(e) => setForm({ ...form, wabtec: e.target.checked })}
                  className="accent-[var(--accent)]"
                  data-testid="ib-wabtec"
                />
                Wabtec-supplied?
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="ib-cancel">{t("common.cancel")}</button>
            <button onClick={save} className="btn-primary" data-testid="ib-save">{t("common.save")}</button>
          </div>
        </Modal>
      )}

      {exportOpen && (
        <ExportColumnsModal
          entityKey="installed_base"
          filters={filters}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  );
}

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(8px)" }} onClick={onClose}>
      <div className="tile p-5 md:p-7 w-full max-w-2xl fade-in" onClick={(e) => e.stopPropagation()} data-testid={testId} style={{ boxShadow: "var(--shadow-lg)" }}>
        <h3 className="text-[20px] font-semibold mb-5 tracking-tight">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, v, set, type = "text" }) {
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <input type={type} className="input-tech" value={v ?? ""} onChange={(e) => set(e.target.value)} />
    </div>
  );
}
function FieldSel({ label, v, set, opts }) {
  return (
    <div>
      <label className="label-tech block mb-1.5">{label}</label>
      <select className="input-tech" value={v || ""} onChange={(e) => set(e.target.value)}>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
